import { type Filter, type Query } from "@cubejs-client/core"
import { useMemo } from "react"
import {
  MetricsMapping,
  type PostProcessResultSync,
} from "~/ui-rtk/constants/metrics-mapping"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"
import useDepVar from "~/ui-rtk/hooks/dep-var"
import {
  convertToCubeFilters,
  getPivotData,
  polyfillCubeQueryWithTimeDimension,
} from "~/ui-rtk/utils/cube"

const AD_PREFIX = "company_media_metrics_ads"

/** The ad-level numbers the Paid Media Optimization Ads tab shows, keyed the way its postProcess emits them. */
export interface AdMetricsRow {
  ad_id: string
  ad_name?: string
  adset_name?: string
  campaign_name?: string
  ad_status?: string
  media_channel?: string
  spend?: number
  impressions?: number
  clicks?: number
  purchases?: number
  short_term_revenue_roas?: number
  brand_value_agg?: number
  brand_value_over_spend?: number
  long_term_revenue?: number
  long_term_revenue_roas?: number
  likes?: number
  comments?: number
  shares?: number
  saves?: number
  follows?: number
}

type NumericKey =
  | "spend"
  | "impressions"
  | "clicks"
  | "purchases"
  | "short_term_revenue_roas"
  | "brand_value_agg"
  | "brand_value_over_spend"
  | "long_term_revenue"
  | "long_term_revenue_roas"
  | "likes"
  | "comments"
  | "shares"
  | "saves"
  | "follows"

const NUMERIC_KEYS: NumericKey[] = [
  "spend",
  "impressions",
  "clicks",
  "purchases",
  "short_term_revenue_roas",
  "brand_value_agg",
  "brand_value_over_spend",
  "long_term_revenue",
  "long_term_revenue_roas",
  "likes",
  "comments",
  "shares",
  "saves",
  "follows",
]

/**
 * The Paid Media Optimization Ads tab's own query, run for a given list of
 * Meta ad ids over the comment report's window, so the ads table on the
 * comment moderation page carries the same spend / revenue / ROAS numbers
 * the Ads tab shows (same measures, same dep-var filters). Impressions and
 * clicks are added on top; the Ads tab does not select them.
 */
export default function useAdMetrics(
  adIds: string[],
  from: string,
  to?: string,
): { metricsByAd: Map<string, AdMetricsRow>; isLoading: boolean } {
  const { depVar, depVarScaler } = useDepVar()
  const ids = useMemo(() => [...new Set(adIds)].sort(), [adIds])

  const query = useMemo<Query | null>(() => {
    if (ids.length === 0) return null
    // The mapping's `q` is typed as a union (object, array or function); the
    // Ads tab entry is a plain query object, as every reuser assumes.
    const q = MetricsMapping.brand_media_optimization_ads_table
      .q as Partial<Query>
    const filters: Filter[] = [
      ...(convertToCubeFilters(AD_PREFIX, {
        dep_var: depVar,
        dep_var_scaler: depVarScaler,
      }) as Filter[]),
      { member: `${AD_PREFIX}.ad_id`, operator: "equals", values: ids },
    ]
    const start = new Date(from)
    const end = to ? new Date(to) : new Date()
    return polyfillCubeQueryWithTimeDimension(
      {
        measures: [
          ...(q.measures ?? []),
          `${AD_PREFIX}.impressions`,
          `${AD_PREFIX}.clicks`,
        ],
        dimensions: [
          `${AD_PREFIX}.ad_id`,
          `${AD_PREFIX}.ad_name`,
          `${AD_PREFIX}.adset_name`,
          `${AD_PREFIX}.campaign_name`,
          `${AD_PREFIX}.ad_status`,
          `${AD_PREFIX}.media_channel`,
        ],
        timeDimensions: q.timeDimensions,
        filters,
        limit: Math.min(1000, ids.length * 4),
      } as Query,
      { dateRange: [start, end] },
    )
  }, [ids, from, to, depVar, depVarScaler])

  const { resultSet, isLoading } = useCubeQuery(query ?? {}, { skip: !query })

  const metricsByAd = useMemo(() => {
    const map = new Map<string, AdMetricsRow>()
    if (!resultSet || !query) return map
    const { postProcess } = MetricsMapping.brand_media_optimization_ads_table
    const pivot = postProcess?.(getPivotData(resultSet, query), {}) as
      | PostProcessResultSync
      | undefined
    for (const raw of (pivot?.[0] ?? []) as Array<Record<string, unknown>>) {
      const adId = String(raw.ad_id ?? "")
      if (!adId) continue
      // One row per (ad, status, channel…) combination can come back; sum
      // the numbers into one line per ad like the Ads tab does per row key.
      const row: AdMetricsRow = map.get(adId) ?? {
        ad_id: adId,
        ad_name: raw.ad_name as string | undefined,
        adset_name: raw.adset_name as string | undefined,
        campaign_name: raw.campaign_name as string | undefined,
        ad_status: raw.ad_status as string | undefined,
        media_channel: raw.media_channel as string | undefined,
      }
      for (const key of NUMERIC_KEYS) {
        const value = Number(raw[key] ?? 0)
        if (!Number.isFinite(value)) continue
        row[key] = (row[key] ?? 0) + value
      }
      map.set(adId, row)
    }
    // Ratios do not add up across rows; derive them from the summed parts.
    for (const row of map.values()) {
      const spend = row.spend ?? 0
      row.short_term_revenue_roas = spend > 0 ? (row.purchases ?? 0) / spend : 0
      row.brand_value_over_spend =
        spend > 0 ? (row.brand_value_agg ?? 0) / spend : 0
      row.long_term_revenue_roas =
        spend > 0 ? (row.long_term_revenue ?? 0) / spend : 0
    }
    return map
  }, [resultSet, query])

  return { metricsByAd, isLoading: !!query && isLoading }
}
